import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate  } from "react-router-dom";
import ChatWidget from "./ChatWidget";
import BrandMark from "./components/common/BrandMark";
import TasksPage from "./components/TasksPage";
import CreateOrganization from "./components/CreateOrganization";
import UploadDocument from "./components/UploadDocument";
import CreateUser from "./components/CreateUser";
import CreateDepartment from "./components/CreateDepartment";
import UserManagement from "./components/admin/UserManagement";
import ProtectedRoute from "./components/common/ProtectedRoute";
import KPIExtraction from "./components/KPIExtraction";
import KPIDashboard from "./components/KPIDashboard";
import LoginPage from "./components/LoginPage";
// Financial module imports
import FinancialDashboard from "./components/financial/FinancialDashboard";
import LedgerManagement from "./components/financial/LedgerManagement";
import InvoiceManagement from "./components/financial/InvoiceManagement";
import ExpenseManagement from "./components/financial/ExpenseManagement";
import CustomerManagement from "./components/financial/CustomerManagement";
import GatePassManagement from "./components/financial/GatePassManagement";
import PaymentsManagement from "./components/financial/PaymentsManagement";
import GDManagement from "./components/financial/GDManagement";
import ReportsManagement from "./components/financial/ReportsManagement";
import CreditManagement from "./components/financial/CreditManagement";
import RemindersPage from "./components/financial/RemindersPage";
import ExchangeRateManagement from "./components/financial/ExchangeRateManagement";
import ShipmentCostManagement from "./components/financial/ShipmentCostManagement";
import ProductManagement from "./components/financial/ProductManagement";
import PurchasesManagement from "./components/financial/PurchasesManagement";
import SalesPurchasesSummary from "./components/financial/SalesPurchasesSummary";
import SalesPurchasesDashboard from "./components/financial/SalesPurchasesDashboard"; // kept for future use
import BatchManagement from "./components/financial/BatchManagement";
import { validateSession, clearAuthData } from "./utils/authUtils";
import { API_BASE } from "./utils/apiUtils";
import { reminderService } from "./services/financeService";
import { getGroupedNavigationItems, getTabValue, getStatusInfo } from "./utils/navigationUtils";
import './styles/App.css';
import './styles/Forms.css';

import {
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from "@mui/material";
import {
  Chat as ChatIcon,
  Business as BusinessIcon,
  CloudUpload as UploadIcon,
  PersonAdd as PersonAddIcon,
  AccountTree as DepartmentIcon,
  Assessment as AssessmentIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  Logout as LogoutIcon,
  AccountBalance as MoneyIcon,
  Receipt as InvoiceIcon,
  MonetizationOn as ExpenseIcon,
  People as PeopleIcon,
  Assignment as GatePassIcon,
  AccountBalanceWallet as PaymentsIcon,
  Description as GDIcon,
  Summarize as ReportsIcon,
  CreditScore as CreditIcon,
  NotificationsActive as RemindersIcon,
  CurrencyExchange as ExchangeRateIcon,
  CalculateOutlined as CostingIcon,
  Category as ProductIcon,
  ShoppingCart as PurchaseIcon,
  SwapHoriz as SwapIcon,
  Inventory2 as BatchIcon,
  ExpandMore as ExpandMoreIcon
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

// Helper function to get icon components
const getIconComponent = (iconName) => {
  switch (iconName) {
    case 'ChatIcon': return <ChatIcon />;
    case 'PersonAddIcon': return <PersonAddIcon />;
    case 'DepartmentIcon': return <DepartmentIcon />;
    case 'BusinessIcon': return <BusinessIcon />;
    case 'UploadIcon': return <UploadIcon />;
    case 'AssessmentIcon': return <AssessmentIcon />;
    case 'MoneyIcon': return <MoneyIcon />;
    case 'InvoiceIcon': return <InvoiceIcon />;
    case 'ExpenseIcon': return <ExpenseIcon />;
    case 'PeopleIcon': return <PeopleIcon />;
    case 'GatePassIcon': return <GatePassIcon />;
    case 'PaymentsIcon': return <PaymentsIcon />;
    case 'GDIcon': return <GDIcon />;
    case 'ReportsIcon': return <ReportsIcon />;
    case 'CreditIcon': return <CreditIcon />;
    case 'RemindersIcon': return <RemindersIcon />;
    case 'ExchangeRateIcon': return <ExchangeRateIcon />;
    case 'CostingIcon': return <CostingIcon />;
    case 'ProductIcon': return <ProductIcon />;
    case 'PurchaseIcon': return <PurchaseIcon />;
    case 'SwapIcon': return <SwapIcon />;
    case 'BatchIcon': return <BatchIcon />;
    case 'SettingsIcon': return <SettingsIcon />;
    default: return <ChatIcon />;
  }
};

const UrgentBadge = ({ count }) =>
  count > 0 ? (
    <span className="nav-badge" aria-label={`${count} urgent`}>
      {count > 99 ? '99+' : count}
    </span>
  ) : null;

const NavigationTabs = ({ urgentCount = 0, onUrgentCount }) => {
  const location = useLocation();
  const [openGroup, setOpenGroup] = useState(null);
  const navRef = React.useRef(null);
  const userData = localStorage.getItem('userData');
  const groups = getGroupedNavigationItems(userData);
  const currentPath = location.pathname;

  // Fetch reminder count on mount and every 5 minutes
  React.useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await reminderService.getCount();
        if (res.success && onUrgentCount) onUrgentCount(res.urgent || 0);
      } catch { /* non-critical — silently skip if user has no finance role */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [onUrgentCount]);

  // Reset badge when visiting reminders page
  React.useEffect(() => {
    if (location.pathname === '/reminders' && onUrgentCount) onUrgentCount(0);
  }, [location.pathname, onUrgentCount]);

  // Close dropdown when clicking outside the nav
  React.useEffect(() => {
    const handleOutsideClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenGroup(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // Close dropdown on route change
  React.useEffect(() => { setOpenGroup(null); }, [location.pathname]);

  const toggle = (i) => setOpenGroup(prev => prev === i ? null : i);

  return (
    <nav className="navbar-nav navbar-primary" ref={navRef} aria-label="Main">
      {groups.map((group, i) => {
        if (group.single) {
          const item = group.items[0];
          return (
            <Link key={i} to={item.path}
              className={`navbar-nav-item ${currentPath === item.path ? 'active' : ''}`}
              aria-current={currentPath === item.path ? 'page' : undefined}>
              <span className="navbar-nav-item-icon" aria-hidden="true">{getIconComponent(item.icon)}</span>
              {item.label}
              {item.path === '/reminders' && <UrgentBadge count={urgentCount} />}
            </Link>
          );
        }
        const isGroupActive = group.items.some(it => it.path === currentPath);
        const isOpen = openGroup === i;
        return (
          <div key={i} className="nav-group">
            <button
              className={`navbar-nav-item nav-group-btn ${isGroupActive ? 'active' : ''} ${isOpen ? 'open' : ''}`}
              onClick={() => toggle(i)}
              aria-expanded={isOpen}
              aria-haspopup="true">
              <span className="navbar-nav-item-icon" aria-hidden="true">{getIconComponent(group.icon)}</span>
              {group.label}
              <span className="nav-group-arrow" aria-hidden="true"><ExpandMoreIcon /></span>
            </button>
            {isOpen && (
              <div className="nav-group-dropdown">
                <div className="nav-dropdown-group-label">{group.label}</div>
                {group.items.map((item, j) => (
                  <Link key={j} to={item.path}
                    className={`nav-dropdown-item ${currentPath === item.path ? 'active' : ''}`}
                    aria-current={currentPath === item.path ? 'page' : undefined}
                    onClick={() => setOpenGroup(null)}>
                    <span className="nav-dropdown-icon" aria-hidden="true">{getIconComponent(item.icon)}</span>
                    {item.label}
                    {item.path === '/reminders' && <UrgentBadge count={urgentCount} />}
                  </Link>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};

const MobileNavigation = ({ open, onClose, onLogout, urgentCount = 0 }) => {
  const location = useLocation();
  const userData = localStorage.getItem('userData');
  const groups = getGroupedNavigationItems(userData);
  const currentPath = location.pathname;

  return (
    <>
      <div className={`mobile-nav-overlay ${open ? 'open' : ''}`} onClick={onClose} />
      <div className={`mobile-nav-panel ${open ? 'open' : ''}`} aria-hidden={!open}>
        <div className="mobile-nav-header">
          <div className="navbar-brand">
            <div className="navbar-logo"><BrandMark /></div>
            <span className="navbar-title">InventoryOS</span>
          </div>
          <button className="mobile-nav-close" onClick={onClose} aria-label="Close menu"><CloseIcon /></button>
        </div>

        <nav className="mobile-nav-list" aria-label="Main">
          {groups.map((group, i) => (
            <div key={i}>
              {!group.single && (
                <div className="mobile-nav-group-label">{group.label}</div>
              )}
              {group.items.map((item, j) => (
                <Link key={j} to={item.path}
                  className={`mobile-nav-item ${currentPath === item.path ? 'active' : ''}`}
                  aria-current={currentPath === item.path ? 'page' : undefined}
                  onClick={onClose}>
                  <span className="mobile-nav-item-icon" aria-hidden="true">{getIconComponent(item.icon)}</span>
                  {item.label}
                  {item.path === '/reminders' && <UrgentBadge count={urgentCount} />}
                </Link>
              ))}
            </div>
          ))}

          <div className="mobile-nav-footer">
            <button className="mobile-nav-item mobile-nav-logout" onClick={onLogout}>
              <span className="mobile-nav-item-icon" aria-hidden="true"><LogoutIcon /></span>
              Log out
            </button>
          </div>
        </nav>
      </div>
    </>
  );
};

const StatusChip = () => {
  const location = useLocation();
  const status = getStatusInfo(location.pathname);

  return (
    <div className={`status-indicator ${status.color}`}>
      <div className="status-dot" aria-hidden="true"></div>
      {status.label}
    </div>
  );
};

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [urgentReminderCount, setUrgentReminderCount] = useState(0);
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const handleValidateSession = async () => {
      const result = await validateSession();
      setIsAuthenticated(result.isAuthenticated);
      setLoading(false);
    };

    handleValidateSession();
  }, []);

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLogin = (token) => {
    localStorage.setItem('authToken', token);
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch (_) { /* ignore — still clear client side */ }
    clearAuthData();
    setIsAuthenticated(false);
    window.location.href = '/login';
  };

  // Get user data for display
  const userData = localStorage.getItem('userData');
  const user = userData ? JSON.parse(userData) : null;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <span className="loading-text">Loading...</span>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return (
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <div className="app-layout">
        <a href="#main-content" className="skip-link">Skip to content</a>
        <header className="app-navbar">
          <div className="navbar-content">
            {/* Mobile menu button */}
            {isMobile && (
              <button
                className="mobile-menu-button"
                onClick={handleMobileMenuToggle}
                aria-label="Open menu"
                aria-expanded={mobileMenuOpen}
              >
                <MenuIcon />
              </button>
            )}
            
            {/* Brand */}
            <div className="navbar-brand">
              <div className="navbar-logo">
                <BrandMark />
              </div>
              <div>
                <span className="navbar-title">InventoryOS</span>
                <p className="navbar-subtitle">Inventory & Trade Management</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            {!isMobile && <NavigationTabs urgentCount={urgentReminderCount} onUrgentCount={setUrgentReminderCount} />}

            {/* Right side */}
            <div className="navbar-nav navbar-actions">
              <StatusChip />
              <span className="navbar-divider" aria-hidden="true" />

              {/* User Menu */}
              {user && (
                <div className="user-menu">
                  <div className="user-menu-button">
                    <div className="user-avatar">
                      {user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                    </div>
                    <div className="user-info">
                      <p className="user-name">{user.email}</p>
                      <p className="user-role">{user.departmentName}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Logout Button */}
              {!isMobile && (
                <button
                  className="navbar-nav-item navbar-icon-btn"
                  onClick={handleLogout}
                  title="Log out"
                  aria-label="Log out"
                >
                  <span className="navbar-nav-item-icon" aria-hidden="true"><LogoutIcon /></span>
                </button>
              )}
            </div>
          </div>
        </header>

        {/* Mobile Navigation */}
        <MobileNavigation
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          onLogout={handleLogout}
          urgentCount={urgentReminderCount}
        />

        {/* Main Content */}
        <main className="main-content" id="main-content" tabIndex={-1}>
          <div className="content-card">
            <Routes>
              <Route path="/" element={<ChatWidget />} />
              {/* Financial Module Routes — each wrapped with its per-user feature-block guard
                  (defense in depth; FeatureAccessFilter on the backend is the real enforcement).
                  LEDGER/CREDIT/SALES_PURCHASES share a backend surface with a sibling feature
                  (see FeatureAccessFilter's doc comment) — still guarded here for UI consistency. */}
              <Route path="/finance" element={<ProtectedRoute require="feature" featureKey="FINANCE_DASHBOARD"><FinancialDashboard /></ProtectedRoute>} />
              <Route path="/ledger" element={<ProtectedRoute require="feature" featureKey="LEDGER"><LedgerManagement /></ProtectedRoute>} />
              <Route path="/invoices" element={<ProtectedRoute require="feature" featureKey="INVOICES"><InvoiceManagement /></ProtectedRoute>} />
              <Route path="/expenses" element={<ProtectedRoute require="feature" featureKey="EXPENSES"><ExpenseManagement /></ProtectedRoute>} />
              <Route path="/customers" element={<ProtectedRoute require="feature" featureKey="CUSTOMERS"><CustomerManagement /></ProtectedRoute>} />
              <Route path="/gate-passes" element={<ProtectedRoute require="feature" featureKey="GATE_PASSES"><GatePassManagement /></ProtectedRoute>} />
              <Route path="/payments" element={<ProtectedRoute require="feature" featureKey="PAYMENTS"><PaymentsManagement /></ProtectedRoute>} />
              <Route path="/gd" element={<ProtectedRoute require="feature" featureKey="GD"><GDManagement /></ProtectedRoute>} />
              <Route path="/reports" element={<ProtectedRoute require="feature" featureKey="REPORTS"><ReportsManagement /></ProtectedRoute>} />
              <Route path="/credit" element={<ProtectedRoute require="feature" featureKey="CREDIT"><CreditManagement /></ProtectedRoute>} />
              <Route path="/reminders" element={<ProtectedRoute require="feature" featureKey="REMINDERS"><RemindersPage /></ProtectedRoute>} />
              <Route path="/exchange-rates" element={<ProtectedRoute require="feature" featureKey="EXCHANGE_RATES"><ExchangeRateManagement /></ProtectedRoute>} />
              <Route path="/costing" element={<ProtectedRoute require="feature" featureKey="COSTING"><ShipmentCostManagement /></ProtectedRoute>} />
              {/* Wastage merged into Batch → Grading tab 2026-08-18 (one place, batch-traceable) — old standalone page retired, redirect anyone with the link bookmarked. */}
              <Route path="/wastage" element={<Navigate to="/batches" replace />} />
              <Route path="/products" element={<ProtectedRoute require="feature" featureKey="PRODUCTS"><ProductManagement /></ProtectedRoute>} />
              <Route path="/purchases" element={<ProtectedRoute require="feature" featureKey="PURCHASES"><PurchasesManagement /></ProtectedRoute>} />
              <Route path="/sales-purchases" element={<ProtectedRoute require="feature" featureKey="SALES_PURCHASES"><SalesPurchasesSummary /></ProtectedRoute>} />
              <Route path="/batches" element={<ProtectedRoute require="feature" featureKey="BATCHES"><BatchManagement /></ProtectedRoute>} />
              {/* Admin Routes */}
              <Route path="/user-management" element={<ProtectedRoute require="admin"><UserManagement /></ProtectedRoute>} />
              <Route path="/create-user" element={<ProtectedRoute require="admin"><CreateUser /></ProtectedRoute>} />
              <Route path="/create-department" element={<ProtectedRoute require="superAdmin"><CreateDepartment /></ProtectedRoute>} />
              <Route path="/create-org" element={<ProtectedRoute require="superAdmin"><CreateOrganization /></ProtectedRoute>} />
              <Route path="/upload" element={<ProtectedRoute require="admin"><UploadDocument /></ProtectedRoute>} />
              <Route path="/kpi-extraction" element={<ProtectedRoute require="admin"><KPIExtraction /></ProtectedRoute>} />
              <Route path="/kpi-dashboard" element={<ProtectedRoute require="admin"><KPIDashboard /></ProtectedRoute>} />
              <Route path="/tasks" element={<TasksPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        </main>
      </div>
    </Router>
  );
}

export default App;